import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  CheckCircle2,
  Circle,
  Coins,
  Flame,
  Gift,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const isFiniteNumber = (value) => (
  typeof value === 'number' && Number.isFinite(value)
);

const formatNumber = (value) => (
  isFiniteNumber(value) ? value.toLocaleString() : '—'
);

const challengeStatus = (challenge) => {
  if (challenge?.claimed === true) return 'Claimed';
  if (challenge?.completed === true) return 'Ready to claim';
  return 'In progress';
};

const DashboardGamificationHub = () => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState('loading');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await authFetch(`${BACKEND_URL}/api/gamification/me`);
      if (!response.ok) {
        throw new Error(`Gamification request failed with status ${response.status}`);
      }
      const data = await response.json();
      if (!data || typeof data !== 'object') {
        throw new Error('Gamification response was not usable');
      }
      setPayload(data);
      setStatus('ready');
    } catch (error) {
      console.error('Failed to fetch dashboard gamification:', error);
      setPayload(null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const challenges = Array.isArray(payload?.daily_challenges)
    ? payload.daily_challenges
    : null;
  const achievements = Array.isArray(payload?.achievements)
    ? payload.achievements
    : null;
  const perks = Array.isArray(payload?.active_perks)
    ? payload.active_perks
    : null;
  const completedChallenges = challenges
    ? challenges.filter((challenge) => challenge?.completed === true).length
    : null;
  const claimedChallenges = challenges
    ? challenges.filter((challenge) => challenge?.claimed === true).length
    : null;
  const unlockedAchievements = achievements
    ? achievements.filter((achievement) => achievement?.unlocked === true).length
    : null;

  return (
    <section
      className="mb-8 rounded-3xl border border-violet-400/20 bg-slate-950/45 p-4 sm:p-6 shadow-xl shadow-violet-950/10"
      aria-labelledby="dashboard-gamification-title"
      data-testid="dashboard-gamification-hub"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-violet-200">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">Your journey</span>
          </div>
          <h2 id="dashboard-gamification-title" className="text-xl font-semibold text-white sm:text-2xl">
            Progress, quests & rewards
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-300">
            Your current rewards data, with quick paths to the journeys already available in SmarterYou.
          </p>
        </div>

        {status === 'ready' && perks && perks.length > 0 && (
          <div className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-medium text-emerald-100">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            {perks.length} active {perks.length === 1 ? 'perk' : 'perks'}
          </div>
        )}
      </div>

      {status === 'loading' && (
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" role="status" aria-label="Loading rewards and challenges">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="min-h-28 rounded-2xl border border-white/10 bg-white/[0.04] p-4 animate-pulse">
              <div className="mb-4 h-4 w-20 rounded bg-white/10" />
              <div className="h-7 w-14 rounded bg-white/15" />
            </div>
          ))}
          <span className="sr-only">Loading your rewards and daily challenges…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-5 rounded-2xl border border-rose-400/25 bg-rose-500/10 p-4" role="alert">
          <p className="font-medium text-rose-100">Rewards data is unavailable right now.</p>
          <p className="mt-1 text-sm text-rose-100/80">
            Your current coins, streak, achievements, and daily challenges could not be loaded.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-rose-200/30 px-3 py-2 text-sm font-medium text-white transition hover:bg-rose-100/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-200"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </button>
        </div>
      )}

      {status === 'ready' && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-400/15 to-orange-500/10 p-4">
              <div className="flex items-center gap-2 text-amber-200">
                <Coins className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide">Coins</span>
              </div>
              <p className="mt-3 text-2xl font-semibold text-white">{formatNumber(payload?.coins)}</p>
              <p className="mt-1 text-xs text-amber-100/75">Current balance</p>
            </div>

            <div className="rounded-2xl border border-orange-400/20 bg-gradient-to-br from-orange-400/15 to-rose-500/10 p-4">
              <div className="flex items-center gap-2 text-orange-200">
                <Flame className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide">Streak</span>
              </div>
              <p className="mt-3 text-2xl font-semibold text-white">
                {isFiniteNumber(payload?.streak_current) ? `${payload.streak_current} days` : '—'}
              </p>
              <p className="mt-1 text-xs text-orange-100/75">
                {isFiniteNumber(payload?.streak_best) ? `Best: ${payload.streak_best} days` : 'Best unavailable'}
              </p>
            </div>

            <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400/15 to-blue-500/10 p-4">
              <div className="flex items-center gap-2 text-cyan-100">
                <Target className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide">Daily challenges</span>
              </div>
              <p className="mt-3 text-2xl font-semibold text-white">
                {challenges ? `${completedChallenges} / ${challenges.length}` : '—'}
              </p>
              <p className="mt-1 text-xs text-cyan-100/75">
                {challenges ? `${claimedChallenges} claimed` : 'Challenges unavailable'}
              </p>
            </div>

            <div className="rounded-2xl border border-violet-400/20 bg-gradient-to-br from-violet-400/15 to-fuchsia-500/10 p-4">
              <div className="flex items-center gap-2 text-violet-100">
                <Award className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide">Achievements</span>
              </div>
              <p className="mt-3 text-2xl font-semibold text-white">
                {achievements ? `${unlockedAchievements} / ${achievements.length}` : '—'}
              </p>
              <p className="mt-1 text-xs text-violet-100/75">Unlocked so far</p>
            </div>
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(16rem,0.55fr)]">
            <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-cyan-200" aria-hidden="true" />
                  <h3 className="font-semibold text-white">Today’s challenges</h3>
                </div>
                {challenges && (
                  <span className="text-xs text-slate-400">{challenges.length} available</span>
                )}
              </div>

              {challenges === null ? (
                <p className="mt-4 text-sm text-slate-400">Daily challenges are unavailable for this account.</p>
              ) : challenges.length === 0 ? (
                <p className="mt-4 text-sm text-slate-400">There are no daily challenges available right now.</p>
              ) : (
                <ul className="mt-3 divide-y divide-white/10">
                  {challenges.slice(0, 3).map((challenge, index) => {
                    const canShowProgress = isFiniteNumber(challenge?.progress) && isFiniteNumber(challenge?.target);
                    const label = typeof challenge?.title === 'string' && challenge.title.trim()
                      ? challenge.title
                      : `Daily challenge ${index + 1}`;
                    return (
                      <li key={challenge?.id || `${label}-${index}`} className="flex items-center gap-3 py-3 first:pt-1 last:pb-0">
                        {challenge?.claimed === true || challenge?.completed === true ? (
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" aria-hidden="true" />
                        ) : (
                          <Circle className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-100">{label}</p>
                          <p className="mt-0.5 text-xs text-slate-400">
                            {canShowProgress ? `${challenge.progress} of ${challenge.target}` : challengeStatus(challenge)}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-slate-300">{challengeStatus(challenge)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-200" aria-hidden="true" />
                <h3 className="font-semibold text-white">Keep moving</h3>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                Pick up where you left off with quests, rewards, or your full progress view.
              </p>
            </div>
          </div>
        </>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <button
          type="button"
          onClick={() => navigate('/quests')}
          className="group flex min-h-12 items-center justify-between rounded-xl border border-cyan-400/20 bg-cyan-400/5 px-4 py-3 text-left transition hover:border-cyan-300/50 hover:bg-cyan-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-cyan-50"><Target className="h-4 w-4" aria-hidden="true" /> Explore quests</span>
          <ArrowRight className="h-4 w-4 text-cyan-200 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/shop')}
          className="group flex min-h-12 items-center justify-between rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-left transition hover:border-amber-300/50 hover:bg-amber-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-50"><Gift className="h-4 w-4" aria-hidden="true" /> View rewards</span>
          <ArrowRight className="h-4 w-4 text-amber-200 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/progress')}
          className="group flex min-h-12 items-center justify-between rounded-xl border border-violet-400/20 bg-violet-400/5 px-4 py-3 text-left transition hover:border-violet-300/50 hover:bg-violet-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-200"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-violet-50"><Trophy className="h-4 w-4" aria-hidden="true" /> View progress</span>
          <ArrowRight className="h-4 w-4 text-violet-200 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

export default DashboardGamificationHub;
